import React, { useState } from "react";
import { toast } from "sonner";
import { DollarSign, Receipt, Wallet, Clock, CreditCard, CheckCircle2 } from "lucide-react";
import { adminApi } from "@/admin/api";

const money = (n) => `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const Card = ({ icon: Icon, value, label, accent, testid }) => (
  <div data-testid={testid} className="bg-[#1A1A1A] border border-[#374151] p-6">
    <Icon className={`h-5 w-5 ${accent ? "text-[#D86C1E]" : "text-white/60"}`} />
    <div className={`font-heading text-3xl font-black mt-4 ${accent ? "text-[#D86C1E]" : "text-white"}`}>
      {value}
    </div>
    <div className="text-xs uppercase tracking-wider text-white/50 mt-1">{label}</div>
  </div>
);

export default function BillingTab({ data, isClient, onPaid }) {
  const [paying, setPaying] = useState(false);

  if (!data) {
    return <div className="text-white/40 py-16 text-center animate-pulse">Loading billing…</div>;
  }

  const pay = async () => {
    setPaying(true);
    try {
      const res = await adminApi.post("/billing/checkout", { origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start payment.");
      setPaying(false);
    }
  };

  return (
    <div data-testid="billing-tab" className="space-y-8">
      {/* Plan banner */}
      <div className="bg-gradient-to-r from-[#1A1A1A] to-[#0A0A0A] border border-[#374151] p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <span
                data-testid="billing-phase"
                className={`text-xs font-heading font-bold uppercase tracking-widest px-3 py-1 ${
                  data.phase === "trial"
                    ? "bg-[#D86C1E]/15 text-[#D86C1E]"
                    : "bg-emerald-500/15 text-emerald-400"
                }`}
              >
                {data.phase === "trial" ? "Trial Plan" : "Standard Plan"}
              </span>
              {data.phase === "trial" && (
                <span className="text-xs text-white/40">{data.trial_days_left} days left</span>
              )}
            </div>
            <h3 className="font-heading text-2xl font-black text-white mt-3">
              {data.phase === "trial" ? (
                <>{money(data.trial_lead_rate)} <span className="text-white/50 text-lg">/ lead</span></>
              ) : (
                <>
                  {money(data.monthly_fee)} <span className="text-white/50 text-lg">/ mo</span>{" "}
                  <span className="text-white/30 text-lg">+</span> {money(data.standard_lead_rate)}{" "}
                  <span className="text-white/50 text-lg">/ lead</span>
                </>
              )}
            </h3>
            <p className="text-sm text-white/50 mt-2 max-w-lg">
              {data.phase === "trial" ? (
                <>
                  3-month trial: a flat {money(data.trial_lead_rate)} per lead, no monthly fee. On{" "}
                  {fmtDate(data.trial_end)} this switches to {money(data.monthly_fee)}/mo +{" "}
                  {money(data.standard_lead_rate)} per lead.
                </>
              ) : (
                <>Standard plan active: {money(data.monthly_fee)} monthly subscription plus {money(data.standard_lead_rate)} per lead.</>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card icon={Receipt} value={data.billable_leads} label="Billable Leads" testid="bill-leads" />
        <Card icon={DollarSign} value={money(data.this_month_charged)} label="Charged This Month" testid="bill-month" />
        <Card icon={Wallet} value={money(data.total_paid)} label="Total Paid" testid="bill-paid" />
        <Card icon={Clock} value={money(data.outstanding)} label="Outstanding" accent testid="bill-outstanding" />
      </div>

      {/* Pay action (client only) */}
      {isClient && (
        <div className="bg-[#1A1A1A] border border-[#374151] p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <div className="font-heading font-bold text-white">Outstanding Balance</div>
            <div className="text-sm text-white/50">Pay securely with card via Stripe.</div>
          </div>
          {data.outstanding > 0 ? (
            <button
              data-testid="pay-balance-btn"
              onClick={pay}
              disabled={paying}
              className="inline-flex items-center gap-2 bg-[#D86C1E] hover:bg-[#B0551A] disabled:opacity-60 text-white font-heading font-bold uppercase tracking-wide px-6 py-3 transition-colors"
            >
              <CreditCard className="h-4 w-4" />
              {paying ? "Redirecting…" : `Pay ${money(data.outstanding)}`}
            </button>
          ) : (
            <span className="inline-flex items-center gap-2 text-emerald-400 font-heading font-bold uppercase tracking-wide">
              <CheckCircle2 className="h-5 w-5" /> All Paid Up
            </span>
          )}
        </div>
      )}

      {/* Charges ledger */}
      <div className="border border-[#374151]">
        <div className="bg-[#141414] px-6 py-3 grid grid-cols-12 gap-4 text-xs uppercase tracking-wider text-white/40 font-bold">
          <div className="col-span-5">Description</div>
          <div className="col-span-3">Date</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Amount</div>
        </div>
        {data.charges.length === 0 ? (
          <div className="px-6 py-12 text-center text-white/40">No charges yet.</div>
        ) : (
          data.charges.map((c) => (
            <div
              key={c.id}
              data-testid={`charge-${c.id}`}
              className="px-6 py-4 grid grid-cols-12 gap-4 border-t border-[#374151] items-center"
            >
              <div className="col-span-5 text-sm text-white">
                {c.kind === "monthly" ? (
                  <>Monthly subscription <span className="text-white/40">({c.period})</span></>
                ) : (
                  <>Lead — <span className="text-white/70">{c.lead_name || "—"}</span></>
                )}
              </div>
              <div className="col-span-3 text-sm text-white/50">{fmtDate(c.created_at)}</div>
              <div className="col-span-2">
                <span
                  className={`text-xs font-bold uppercase tracking-wide ${
                    c.status === "paid" ? "text-emerald-400" : "text-[#D86C1E]"
                  }`}
                >
                  {c.status}
                </span>
              </div>
              <div className="col-span-2 text-right font-heading font-bold text-white">
                {money(c.amount)}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
